import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Company } from "@/types/company";
import {
  Building2,
  Users,
  Globe,
  Phone,
  MapPin,
  Briefcase,
  ExternalLink,
  ChevronRight,
  Calendar,
  FileText,
} from "lucide-react";

interface CompanyCardProps {
  company: Company;
  onClick: (company: Company) => void;
}

export function CompanyCard({ company, onClick }: CompanyCardProps) {
  const getIndustryName = (): string => {
    if (typeof company.industry === "string") {
      return company.industry;
    } else if (
      company.industry &&
      typeof company.industry === "object" &&
      "name" in company.industry
    ) {
      return company.industry.name;
    } else if (company.industry_id) {
      return company.industry_id;
    }
    return "Sin industria";
  };

  const industryName = getIndustryName();
  const companyLogo = company.logo_url || company.logo;

  const getSizeDisplay = (): string => {
    if (!company.size) return "Sin tamaño";
    if (company.size === "5001-10000") return "5000+";

    const match = company.size.match(/(\d+)\s*-?\s*(\d+)?/);
    if (match) {
      const [, min, max] = match;
      if (max) return `${parseInt(min)}-${parseInt(max)}`;
      return parseInt(min).toString();
    }

    return company.size;
  };

  const formatPhone = (phone: string): string => {
    if (!phone) return "Sin teléfono";
    return phone.replace(/[^\d+]/g, " ");
  };

  const createdAt = (company as any).createdAt || (company as any).created_at;

  return (
    <Card
      className="cursor-pointer hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-slate-200 overflow-hidden group"
      onClick={() => onClick(company)}
    >
      <CardContent className="p-0">
        {/* Header con gradiente */}
        <div className="bg-gradient-to-r from-slate-50 to-white p-6 border-b border-slate-100">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-4">
              <Avatar className="h-14 w-14 border-2 border-white shadow-md">
                <AvatarImage src={companyLogo || ""} className="object-cover" />
                <AvatarFallback className="bg-gradient-to-br from-blue-600 to-purple-600 text-white font-semibold">
                  {company.name.slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-1">
                  <h3 className="font-bold text-lg text-slate-900 truncate group-hover:text-blue-700 transition-colors">
                    {company.name}
                  </h3>
                  <ChevronRight className="h-4 w-4 text-slate-400 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                </div>
                <div className="flex items-center space-x-2 mb-3">
                  <Briefcase className="h-3 w-3 text-slate-500 flex-shrink-0" />
                  <p className="text-sm text-slate-600 truncate">
                    {industryName}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge
                    variant="secondary"
                    className="bg-blue-50 text-blue-700 hover:bg-blue-100 border-blue-200"
                  >
                    <Users className="h-3 w-3 mr-1" />
                    {getSizeDisplay()}
                  </Badge>
                  {company.subsidiaries && company.subsidiaries.length > 0 && (
                    <Badge
                      variant="outline"
                      className="bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border-emerald-200"
                    >
                      {company.subsidiaries.length} subsidiaria
                      {company.subsidiaries.length !== 1 ? "s" : ""}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Información de contacto */}
        <div className="p-6 space-y-2.5">
          {/* Teléfono */}
          <div className="flex items-center text-sm">
            <Phone
              className={`h-4 w-4 mr-2 flex-shrink-0 ${company.phone ? "text-blue-500" : "text-slate-300"}`}
            />
            {company.phone ? (
              <span className="text-slate-600">
                {formatPhone(company.phone)}
              </span>
            ) : (
              <span className="text-slate-400 italic text-xs">
                Teléfono no proporcionado
              </span>
            )}
          </div>

          {/* Sitio Web */}
          <div className="flex items-center text-sm">
            <Globe
              className={`h-4 w-4 mr-2 flex-shrink-0 ${company.website ? "text-emerald-500" : "text-slate-300"}`}
            />
            {company.website ? (
              <a
                href={company.website}
                onClick={(e) => e.stopPropagation()}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 truncate block hover:underline flex items-center"
              >
                {company.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                <ExternalLink className="h-3 w-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
            ) : (
              <span className="text-slate-400 italic text-xs">
                Sitio web no proporcionado
              </span>
            )}
          </div>

          {/* Dirección */}
          <div className="flex items-center text-sm">
            <MapPin
              className={`h-4 w-4 mr-2 flex-shrink-0 ${company.address ? "text-amber-500" : "text-slate-300"}`}
            />
            {company.address ? (
              <span className="text-slate-600 line-clamp-1">
                {company.address}
              </span>
            ) : (
              <span className="text-slate-400 italic text-xs">
                Dirección no proporcionada
              </span>
            )}
          </div>

          {/* Fecha Registro */}
          <div className="flex items-center text-sm">
            <Calendar className="h-4 w-4 mr-2 flex-shrink-0 text-purple-500" />
            <span className="text-xs text-slate-500">
              Registrada:{" "}
              {createdAt ? new Date(createdAt).toLocaleDateString() : "N/A"}
            </span>
          </div>

          {/* Documento RTU */}
          <div className="flex items-center text-sm">
            <FileText
              className={`h-4 w-4 mr-2 flex-shrink-0 ${company.rtu_url ? "text-indigo-500" : "text-slate-300"}`}
            />
            {company.rtu_url ? (
              <a
                href={company.rtu_url}
                onClick={(e) => e.stopPropagation()}
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-600 hover:text-indigo-800 text-xs font-medium truncate block hover:underline flex items-center"
              >
                Ver Documento RTU
                <ExternalLink className="h-3 w-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
            ) : (
              <span className="text-slate-400 italic text-xs">
                RTU no adjunto
              </span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
